import Omnitone from 'omnitone/build/omnitone.min.esm';

/**
 * Runs a function at a specific point in the video.
 * @param {Object} player - A videoJS player instance.
 * @param {Integer} displayAt - Amount in seconds where function should run. It will be used to calculate time before the end of the video where the function should occur.
 * @param {Function} callback - To be ran at a desired point in the video. It will run multiple times until the end of video unless runOnce is specified.
 * @param {Function} elseCallback - Will run every second before time specified.
 * @param {Boolean} runOnce - Prevents callback from running more than once, elseCallback will still run every second before time specified.
 */
export const runAtTime = ({ player, displayAt, callback, elseCallback = null, runOnce = false } = {}) => {
  player.one('loadedmetadata', function () {
    // Used when the function is required to run only once when a time in the video has been reached.
    let allowedExecution = true;
    let duration = player.duration();
    let marker = duration - displayAt;

    // Runs every second on play or when progress bar is interacted with.
    player.on('timeupdate', timeUpdate);
    player.on('player:will-set-variant', teardown);
    player.on('player:unlinked-choice', teardown);

    function timeUpdate() {
      event.stopPropagation();
      const currentTime = Math.floor(player.currentTime());
      if (allowedExecution) {
        if (isNaN(duration)) {
          marker = player.duration() - displayAt;
        }

        if (currentTime >= marker && allowedExecution) {
          callback(currentTime, duration);
          if (runOnce) allowedExecution = false;
        } else {
          elseCallback ? elseCallback() : null;
        }
      }

      if (currentTime == Math.round(player.duration())) {
        teardown();
      }
    }

    function teardown() {
      player.off('timeupdate', timeUpdate);
      player.off('player:will-set-variant', teardown);
    }
  });
};

/**
 * Renders buttons in videoJS's control bar, event can be specified
 * and will run when a callback is provided in the button object.
 *
 * @param {Array} buttons - An array with button definition objects structured for videoJS.
 * @param {Object} parent - videoJS component the buttons will be appended to.
 * @param {String} eventName - JS event is assigned to the element and runs a callback if available. If null, button callbacks will not be tied to an eventListener and will run as soon as possible.
 **/
export const createButtons = (parent, buttons, events = ['mouseup', 'touchend']) => {
  buttons.forEach((button) => {
    const element = parent.addChild('button', button).el_;

    if (typeof button.callback == 'function' && events === null) {
      button.callback();
    }

    if (typeof button.callback == 'function' && events !== null) {
      events.forEach((eventName) => {
        element.addEventListener(
          eventName,
          (event) => {
            button.callback(event);
          },
          false,
        );
      });
    }
  });
};

/**
 * Skips time with a custom amount of seconds and option of
 * moving back.
 * @param {Integer} amount - Amount in seconds time should be skipped.
 * @param {Boolean} back - Will skip time backwards when set to true.
 */
export const skipTime = (player, amount = 30, back = false) => {
  const direction = back ? player.currentTime() - amount : player.currentTime() + amount;

  player.currentTime(direction);
};

/**
 * Sets the scene to any source provided, plays it immediately.
 * @param {Object} player - A videoJS player instance.
 * @param {String} clipSource - A path or url to a video file with supported format.
 */
export const setScene = (player, clipSource, play = false, isFirstPlay) => {
  // Reset the player so the `firstplay` event can be used
  // to detect when the start of an island being played
  // TODO: Maybe find a better option than `reset`. It completely
  // replaces the `<video>` tag inside the browser which:
  // 1. Doesn't sound the most performant thing
  // 2. Requires to re-set the `crossorigin` attribute for 360 videos
  player.reset();

  if (clipSource) {
    const type = clipSource.match(/\.mp4/) ? 'video/mp4' : 'application/x-mpegURL';
    player.src({ src: clipSource, type });
    player.isVideo = true;
    player.controls(true);
    player.el().setAttribute('data-wrp-type', 'video');
    player.crossOrigin('anonymous'); // This gets wiped by the `.reset()`
    if (player.vr) {
      player.mediainfo.projection = player.variant.media_projection || 'NONE';
      const vrOptions = player.vr().options_;
      if (player.variant.media_omnitone) {
        vrOptions.omnitone = Omnitone;
      }

      // Make the player register the change of projection
      player.vr(vrOptions).changeProjection_('AUTO');
    }
    if (play) {
      if (isFirstPlay) {
        player.muted(true);
      }
      player.play();
    }
  } else {
    player.isVideo = false;
    // Hide the controls as we're not showing a video
    player.controls(false);
    // Pause and remove the source from the player
    // to prevent it playing in the background
    // automatically
    player.el().setAttribute('data-wrp-type', 'image');
  }
};

export const hideFullscreen = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (/android/i.test(ua)) {
    return false;
  } else if (/iphone/.test(ua)) {
    return true;
  }
  return false;
};

// TODO: Early implementation of native fullscreen mode that encasulates the whole Stornaway player.
// The following snippet works in Firefox and Chrome but it needs support to be extended to Safari.
// It will also need to be extended to exit full screen mode.
// Fullscreen API resource: https://developer.mozilla.org/en-US/docs/Web/API/Fullscreen_API
export const toggleFullscreen = (enter) => {
  const SELECTOR_PLAYER_WRAPPER = '.wrp-watch-player-wrapper';
  const playerWrapper = document.querySelector(SELECTOR_PLAYER_WRAPPER);
  const FULL_SCREEN_ICONS = '.vjs-wrp-fullscreen';
  const icons = document.querySelectorAll(FULL_SCREEN_ICONS);
  icons.forEach((icon) => icon.classList.toggle('hidden'));
  const isWebkit = /(safari|chrome)/.test(navigator.userAgent.toLowerCase());

  if (enter) {
    if (isWebkit) {
      playerWrapper.webkitRequestFullscreen();
    } else {
      playerWrapper.requestFullscreen();
    }
  } else if (isWebkit) {
    document.webkitExitFullscreen();
  } else {
    document.exitFullscreen();
  }
};

// function isVideoInFullscreen() {
//   !!document.fullscreenElement;
// }

/**
 * Attempts to retrieve control bar position. Used to prevent choices bar from overlapping the control bar.
 * @param {Object} controlBar - A videoJS controlBar instance. e.g player.controlBar
 * @return {Integer}
 */
export const getControlBarHeight = (controlBar) => {
  const elementHeight = parseInt(window.getComputedStyle(controlBar.el(), null).getPropertyValue('height').slice(0, -2));

  const bottomProp = parseInt(window.getComputedStyle(controlBar.el(), null).getPropertyValue('bottom').slice(0, -2));

  return elementHeight + bottomProp;
};

/**
 * Checks whether the alpha value of an rgba color range is transparent. Returns true or false.
 * @param {String} colourVal - Can be a HEX value or RGBA (legacy) e.g "#000 (not transparent), #00000000 (transparent) rgba(0,239.99999999999997,14.999999999999998,0)"
 */
export const isTransparent = (colorVal) => {
  // Used to determine how to format value.
  // Required for legacy projects that used RGBA.
  const isRGBA = colorVal.includes('rgba');
  const isHEXA = colorVal.includes('#');

  // Initial value, if hex code is transparent it will change.
  let alpha = 1;

  if (isRGBA) {
    alpha = parseInt(colorVal.slice(-2)[0]);
  }

  if (isHEXA && colorVal.length > 7) {
    // The last hex character of a large code determines the apha value.
    alpha = parseInt(colorVal.slice(-2)[1]);
  }

  return alpha == 0;
};

/**
 * Determines if a choice is valid by checking if it is linked or with text.
 * Used to understand if a button should be rendered.
 * @param {Object} choice - A JS object with choice data.
 * @return {Boolean}
 */
export const isChoiceValid = (choice) => {
  return choice.label || Object.values(choice.linked_variants).length;
};

/**
 * Sets the quality level of the current video (if available and when possible).
 *
 * @param {Object} player - A videoJS player instance.
 * @param {String} min_quality - Takes 360p, 720p, 1080p, 4k and auto. Will start from that value and go up is possible. Auto will go as high as possible for the player size.
 */
export const setVideoQuality = (player, min_quality = '1080p') => {
  // Lists all supported quality levels to allow them to be set dynamically.
  const quality_index = {
    '360p': 640,
    '720p': 1280,
    '1080p': 1920,
    '4k': 3840,
    auto: player.el().offsetWidth,
  };

  const qualityLevels = player.qualityLevels();
  const screenWidth = quality_index[min_quality];

  for (let i = 0; i < qualityLevels.length; i++) {
    let qualityLevel = qualityLevels[i];
    if (qualityLevel.width >= screenWidth) {
      qualityLevel.enabled = true;
    } else {
      qualityLevel.enabled = false;
    }
  }
};

/**
 * Retrieves the appropriate linked variant to play.
 * @param {Object} choice A choice object of an exported project. Found inside a variant.
 * @param {Array} history The current player's history.
 * @returns {String|Null} The id of the variant that will play next. Can be null if no id can be retrieved.
 */
export function getLinkedVariantId({ choice, history } = {}) {
  return getLink(choice, history)?.id;
}

/**
 * Advanced Logic version for #getLinkedVariantId
 * @param {Object} choice A choice object of an exported project. Found inside a variant.
 * @param {Array} history The current player's history.
 * @param {AdvancedLogicContext} aLContext The current player's history.
 * @returns {String|Null} The id of the variant that will play next. Can be null if no id can be retrieved.
 */
export function getALLinkedVariantId({ choice, aLContext }) {
  const linkedVariant = aLContext.getLinkedVariant({ choice });
  return linkedVariant?.id;
}

/**
 * Returns the links that'll get used for the give choice
 * based on which variants have already been seen by the user
 * @param {Object} choice
 * @param {Array} history
 * @returns
 */
export function getLink(choice, history) {
  const variants = Object.values(choice.linked_variants);
  const numberOfVariants = variants.length;
  const isConditional = numberOfVariants > 1;

  if (isConditional) {
    // Lookup correct linkedVariant to be consumed by setVariant.
    const recentlyViewedId = getMostRecentlyViewed(history, variants);

    // Retrieves the variant which has been most recently viewed.
    const linkedVariant = Object.values(variants).find((variant) => variant.if_viewed_most_recently === recentlyViewedId);

    return linkedVariant || variants.find((variant) => variant.default) || variants[0];
  }

  return variants[0];
}

/**
 * Retrieves all variant ids from each linked variant (from a single choice) that are marked as if_viewed_most_recently.
 * Useful to determine which linked variant to play next when facing a conditional choice (choice with multiple links).
 * @param {Object} linkedVariants
 * @returns {Array} A list of all if_viewed_most_recently ids present in a choice.
 */
function getLinkedVariantIfViewedIds(linkedVariants) {
  const viewedIds = Object.values(linkedVariants).map((variant) => {
    return variant.if_viewed_most_recently;
  });

  return viewedIds;
}

/**
 * Returns the most recently viewed variant out of provided choices.
 * Useful when understanding which linked variant to play next when facing a conditional choice.
 * @param {Array} history Current player history, used to compare against a choice linked variants.
 * @param {Object} linkedVariants Linked variants are found on a choice and used to find the most recently viewed id.
 * @returns {String} An id of most recently viewed variant.
 * @returns {null} If none of the variants were viewed at all.
 */
function getMostRecentlyViewed(history, linkedVariants) {
  // Retrieves all available if_viewed ids for comparison with history.
  const viewedIds = getLinkedVariantIfViewedIds(linkedVariants);

  // Lists the index of each variant in history.
  const variantIndexes = viewedIds.map((variant) => {
    return history.indexOf(variant);
  });

  // The largest index is the most recently viewed.
  const recentlyViewed = history[Math.max(...variantIndexes)];

  // Returns the variant id of the one most recently viewed.
  return recentlyViewed === undefined ? null : recentlyViewed;
}

/**
 * Retrieves current player in use.
 * Useful when access to the instance of a player is required on an unsual place.
 * @param {Array} players - An array with videoJS players. Typically a result of videojs.getAllPlayers().
 * @return {Object} - A videoJS player marked as currently in use.
 */
export function getCurrentPlayer(players) {
  const player = players.filter((player) => {
    return player.currentPlayer == true;
  });
  return player[0];
}

/**
 * Retrieves current innactive player.
 * Useful when access to the instance of a player is required on an unsual place.
 * @param {Array} players - An array with videoJS players. Typically a result of videojs.getAllPlayers().
 * @return {Object} - A videoJS player marked as currently in use.
 */
export function getInactivePlayer(players) {
  const player = players.filter((player) => {
    return player.currentPlayer == false;
  });
  return player[0];
}

/**
 * Checks if a given variant ID exists in the player's history.
 * @param {Array} history Current player history.
 * @param {String} linkedVariantId The variant ID to check for.
 * @returns {Boolean} True if the variant ID exists in the history, otherwise false.
 */
export function isVariantInHistory(linkedVariantId, history) {
  return history.includes(linkedVariantId);
}

/**
 * Retrieves the 'captions_label' and 'show_captions' variables from the context.
 * Ensures safe access to prevent errors if context properties are missing.
 *
 * - If 'captions_label' is missing, defaults to `null`.
 * - If 'show_captions' is missing, defaults to `false`.
 * - Converts 'show_captions' string values ("true" or "false") to a boolean.
 *
 * @param {Object} aLContext - The application context containing variables.
 * @returns {Object} An object with `variableCaptionsLabel` and `variableShowCaptions`.
 *                   Example: { variableCaptionsLabel: "English", variableShowCaptions: true }
 */
export function checkCaptionsVariables(aLContext) {
  if (!aLContext) {
    console.info('aLContext is undefined. Returning default captions values.');
    return {
      variableCaptionsLabel: null,
      variableShowCaptions: false,
    };
  }

  // Retrieve the captions label if available
  let variableCaptionsLabel = aLContext.currentContext.captions_label || null;
  // Retrieve and convert show_captions from string to boolean
  let variableShowCaptions = aLContext.currentContext.show_captions === 'true' || false;

  return {
    variableCaptionsLabel,
    variableShowCaptions,
  };
}
